import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Badge, Modal, Form } from 'react-bootstrap';
import { FaCheckCircle, FaBolt, FaGift, FaStar, FaLock } from 'react-icons/fa';
import '../../Agency/JobSeekerResumeUpdate/AgencySubscriptionPlans.css';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import EmployerNavbar from '../../Pages/Navbar/EmployerNavbar';
import BaseUrl from '../../ApiServices/BaseUrl';
import loadRazorpay from '../../ApiServices/loadRazorpay';

const PLAN_ICONS = { free: <FaGift />, gold: <FaStar />, pay_per_click: <FaBolt />, silver: <FaStar /> };
const PLAN_GRADIENTS = {
  free: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
  gold: 'linear-gradient(135deg, #f4a903 0%, #d48f00 100%)',
  pay_per_click: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
  silver: 'linear-gradient(135deg, #8E9AAF 0%, #5C6B7A 100%)'
};
const PLAN_COLORS = { free: '#6c757d', gold: '#f4a903', pay_per_click: '#0d6efd', silver: '#8E9AAF' };
const PLAN_LABELS = { free: 'FREE', gold: 'GOLD', pay_per_click: 'PAY PER USE', silver: 'SILVER' };
const PLAN_RANK = { free: 0, pay_per_click: 1, silver: 2, gold: 3 };

// Move LoginModal outside the main component to prevent re-renders
const LoginModal = ({
  show, onHide, email, password, errorMsg, showPassword,
  onEmailChange, onPasswordChange, onTogglePassword, onSubmit, isLoading
}) => (
  <Modal show={show} onHide={onHide} centered backdrop="static">
    <Modal.Header closeButton>
      <Modal.Title className="w-100 text-center">
        <h5 className="fw-bold">Login Required</h5>
        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
          Please login to access subscription plans
        </p>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {errorMsg && <div className="alert alert-danger py-2">{errorMsg}</div>}
      <form onSubmit={onSubmit}>
        <div className="mb-3">
          <label className="form-label">Email Address</label>
          <input type="email" className="form-control" placeholder="Email"
            value={email} onChange={onEmailChange} required disabled={isLoading} />
        </div>
        <div className="mb-3 position-relative">
          <label className="form-label">Password</label>
          <input type={showPassword ? "text" : "password"} className="form-control pe-5"
            placeholder="Password" value={password} onChange={onPasswordChange}
            required disabled={isLoading} />
          <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
            onClick={onTogglePassword}
            style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#6c757d" }} />
        </div>
        <button className="w-100 btn btn-success py-2" type="submit" disabled={isLoading}>
          {isLoading ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Logging in...</> : "LOG IN"}
        </button>
      </form>
    </Modal.Body>
  </Modal>
);

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user')) || {};
  const employerId = user.id || '';
  const employerCountry = user.country || 'AE';

  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null); // { applied: boolean, message: string, discount: number }
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const [walletBalance, setWalletBalance] = useState(0);
  const [topUpModal, setTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [processingTopUp, setProcessingTopUp] = useState(false);

  // Login modal states
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginErrorMsg, setLoginErrorMsg] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleEmailChange = useCallback((e) => setLoginEmail(e.target.value), []);
  const handlePasswordChange = useCallback((e) => setLoginPassword(e.target.value), []);
  const togglePasswordVisibility = useCallback(() => setShowLoginPassword(prev => !prev), []);
  const handleCloseModal = useCallback(() => { setShowLoginModal(false); setLoginErrorMsg(""); }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailFromUrl = urlParams.get('email');
    const currentUser = JSON.parse(localStorage.getItem('user'));
    if (emailFromUrl && !currentUser) {
      setLoginEmail(decodeURIComponent(emailFromUrl));
      setShowLoginModal(true);
    }
  }, [location]);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${BaseUrl}/api/employer/subscription-plans?country=${employerCountry}`);
      setPlans(res.data);
    } catch {
      toast.error('Failed to load subscription plans');
    }
  }, [employerCountry]);

  const fetchCurrentSub = useCallback(async () => {
    if (!employerId) return;
    try {
      const res = await axios.get(`${BaseUrl}/api/employer/subscription/${employerId}`);
      setCurrentSub(res.data.subscription);
      setUsage(res.data.usage || []);
    } catch { /* no sub yet */ }
  }, [employerId]);

  const fetchWalletInfo = useCallback(async () => {
    if (!employerId) return;
    try {
      const res = await axios.get(`${BaseUrl}/api/employer/wallet/${employerId}`);
      setWalletBalance(res.data.balance || 0);
    } catch (err) {
      console.error('Error fetching wallet:', err);
    }
  }, [employerId]);

  useEffect(() => {
    Promise.all([fetchPlans(), fetchCurrentSub(), fetchWalletInfo()]).finally(() => setLoading(false));
  }, [fetchPlans, fetchCurrentSub, fetchWalletInfo]);

  const handleLoginSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginErrorMsg("");
    try {
      const res = await fetch(`${BaseUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLoginErrorMsg(data.message || "Login failed");
      } else {
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("userId", data.user.id);
        setShowLoginModal(false);
        setLoginPassword("");
        Swal.fire({ title: 'Login Successful!', text: `Welcome back, ${data.user.first_name}!`, icon: 'success', confirmButtonColor: '#28a745' }).then(() => window.location.reload());
      }
    } catch (error) {
      setLoginErrorMsg("Network error or server is down");
    } finally {
      setIsLoggingIn(false);
    }
  }, [loginEmail, loginPassword]);

  const handleTopUp = async () => {
    if (!topUpAmount || isNaN(topUpAmount) || topUpAmount <= 0) {
      Swal.fire('Error', 'Please enter a valid amount', 'error');
      return;
    }
    setProcessingTopUp(true);
    try {
      const amountInPaise = Math.round(parseFloat(topUpAmount) * 100);
      const orderRes = await axios.post(`${BaseUrl}/api/orders`, { amount: amountInPaise, currency: 'INR' });
      const { order_id, currency, amount: orderAmount } = orderRes.data;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: orderAmount, currency,
        name: 'Gulf Worker', description: 'Wallet Top-up', order_id,
        handler: async (response) => {
          const verifyRes = await axios.post(`${BaseUrl}/api/verify-payment`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });
          if (verifyRes.data.success) {
            await axios.post(`${BaseUrl}/api/employer/wallet/topup`, {
              employer_id: employerId, amount: parseFloat(topUpAmount),
              description: `Razorpay Top-up (${response.razorpay_payment_id})`
            });
            Swal.fire('Success', 'Wallet topped up successfully', 'success');
            setTopUpModal(false); setTopUpAmount(''); fetchWalletInfo();
          } else { Swal.fire('Error', 'Payment verification failed', 'error'); }
        },
        prefill: { name: user.first_name || '', email: user.email || '', contact: user.mobile || '' },
        theme: { color: '#f39c12' },
        modal: { ondismiss: () => { setProcessingTopUp(false); toast.warn('Payment cancelled'); } }
      };
      const isRazorpayReady = await loadRazorpay();
      if (!isRazorpayReady) {
        toast.error('Payment gateway failed to load. Please try again.');
        return;
      }
      new window.Razorpay(options).open();
    } catch (err) {
      toast.error(`Top-up failed: ${err.response?.data?.details || err.message}`);
    } finally { setProcessingTopUp(false); }
  };

  const isSubscriptionActive = () => currentSub?.status === 'active' && new Date(currentSub.expiry_date) >= new Date();
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const getUsage = (featureKey) => { const fm = usage.find(u => u.feature_key === featureKey); return fm ? fm.units_used : 0; };
  const isDowngrade = (plan) => {
    if (!isSubscriptionActive() || !currentSub) return false;
    return (PLAN_RANK[plan.type] ?? 0) < (PLAN_RANK[currentSub.plan_type] ?? 0);
  };

  const handleSubscribe = async (plan) => {
    if (!employerId) { setShowLoginModal(true); return; }
    if (isSubscriptionActive() && currentSub?.plan_id === plan.id) { toast.info('You already have this plan active.'); return; }
    if (isDowngrade(plan)) { toast.error('Downgrade not allowed. Please wait for your current plan to expire.'); return; }
    if (isSubscriptionActive()) {
      let switchMsg = `Your current plan "${currentSub.plan_name}" will be cancelled and replaced with "${plan.name}".`;
      if (plan.type === 'gold' && currentSub.plan_type === 'pay_per_click') switchMsg += '\n\n✅ After upgrading to Gold, your wallet balance will NOT be deducted for usage.';
      if (plan.type === 'silver' && currentSub.plan_type === 'pay_per_click') switchMsg += '\n\n✅ After upgrading to Silver, your wallet balance will NOT be deducted for usage.';
      const result = await Swal.fire({ title: 'Upgrade Plan?', text: switchMsg, icon: 'question', showCancelButton: true, confirmButtonText: 'Yes, upgrade' });
      if (!result.isConfirmed) return;
    }
    const pricing = plan.country_pricing;
    setSelectedPlanId(plan.id);
    const basePrice = pricing ? parseFloat(pricing.base_price) : 0;

    // Calculate final price with coupon if applied
    let finalAmountInPaise = Math.round(basePrice * 100);
    let paymentStatus = 'paid';

    if (couponStatus && couponStatus.applied && (plan.type === 'gold' || plan.type === 'silver')) {
      const discountMultiplier = (100 - couponStatus.discount) / 100;
      finalAmountInPaise = Math.round((basePrice * discountMultiplier) * 100);
      if (finalAmountInPaise === 0) {
        paymentStatus = 'coupon_applied';
      }
    }

    if (plan.type === 'free' || !pricing || basePrice === 0 || finalAmountInPaise === 0) {
      await doSubscribe(plan, paymentStatus === 'coupon_applied' ? 'coupon_applied' : 'free', 0, null, null);
      return;
    }

    setSubscribing(true);
    try {
      const orderRes = await axios.post(`${BaseUrl}/api/orders`, { amount: finalAmountInPaise, currency: 'INR' });
      const { order_id, currency, amount: orderAmount } = orderRes.data;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY, amount: orderAmount, currency,
        name: 'Gulf Worker', description: `${plan.name} Subscription`, order_id,
        handler: async (response) => {
          const verifyRes = await axios.post(`${BaseUrl}/api/verify-payment`, {
            razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });
          if (verifyRes.data.success) await doSubscribe(plan, 'paid', orderAmount / 100, order_id, response.razorpay_payment_id);
          else toast.error('Payment verification failed');
        },
        prefill: { name: user.first_name || '', email: user.email || '', contact: user.mobile || '' },
        theme: { color: '#3399cc' },
        modal: { ondismiss: () => { setSubscribing(false); setSelectedPlanId(null); toast.warn('Payment cancelled'); } }
      };
      const isRazorpayReady = await loadRazorpay();
      if (!isRazorpayReady) {
        toast.error('Payment gateway failed to load. Please try again.');
        setSubscribing(false);
        setSelectedPlanId(null);
        return;
      }
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(`Payment failed: ${e.response?.data?.details || e.message}`);
      setSubscribing(false); setSelectedPlanId(null);
    }
  };

  const doSubscribe = async (plan, paymentStatus, amountPaid, orderId, paymentId) => {
    setSubscribing(true);
    try {
      const pricing = plan.country_pricing;
      await axios.post(`${BaseUrl}/api/employer/subscribe`, {
        employer_id: employerId, plan_id: plan.id,
        country_code: pricing?.country_code || employerCountry, currency: pricing?.currency || 'AED',
        base_price_paid: amountPaid, payment_status: paymentStatus,
        razorpay_order_id: orderId, razorpay_payment_id: paymentId
      });
      await Swal.fire({ title: 'Subscription Activated!', text: `You are now subscribed to "${plan.name}". Valid for ${plan.validity_days} days.`, icon: 'success' });
      await fetchCurrentSub(); await fetchPlans(); await fetchWalletInfo();
    } catch (e) { toast.error(e.response?.data?.error || 'Subscription failed'); }
    finally { setSubscribing(false); setSelectedPlanId(null); }
  };

  if (loading) return (<><EmployerNavbar /><Container style={{ marginTop: '90px' }}><div className="text-center py-5"><Spinner animation="border" /><p className="mt-2">Loading subscription plans...</p></div></Container></>);

  const handleApplyCoupon = async (plan) => {
    if (!employerId) { Swal.fire('Login Required', 'Please login to subscribe.', 'info'); setShowLoginModal(true); return; }
    if (!couponCode) {
      toast.error('Please enter a coupon code');
      return;
    }
    setApplyingCoupon(true);
    setCouponStatus(null);
    try {
      const res = await axios.post(`${BaseUrl}/api/coupon/validate`, { code: couponCode, plan_type: plan.type });
      if (res.data.success) {
        const discount = parseFloat(res.data.discount_percentage);
        setCouponStatus({ applied: true, message: res.data.message, discount: discount });
        setCouponCode('');

        if (discount === 100) {
          toast.success('Coupon Applied! Activating your free plan...');
          await doSubscribe(plan, 'coupon_applied', 0, null, null);
        } else {
          toast.success(`Coupon Applied: ${res.data.message}`);
        }
      } else {
        setCouponStatus({ applied: false, message: res.data.message, discount: 0 });
        toast.error(res.data.message);
      }
    } catch (err) {
      setCouponStatus({ applied: false, message: err.response?.data?.message || 'Invalid coupon', discount: 0 });
      toast.error(err.response?.data?.message || 'Invalid coupon');
    } finally {
      setApplyingCoupon(false);
    }
  };

  return (
    <>
      <EmployerNavbar />

      <LoginModal
        show={showLoginModal} onHide={handleCloseModal}
        email={loginEmail} password={loginPassword} errorMsg={loginErrorMsg}
        showPassword={showLoginPassword} onEmailChange={handleEmailChange}
        onPasswordChange={handlePasswordChange} onTogglePassword={togglePasswordVisibility}
        onSubmit={handleLoginSubmit} isLoading={isLoggingIn}
      />

      <Container className="py-4 asp-compact-cards" style={{ marginTop: '70px', maxWidth: '1600px' }}>
        <div className="asp-page-header">
          <h2 className="asp-page-title">Employer Subscription Plans</h2>
          <p className="asp-page-subtitle">Subscribe to unlock candidate contact details</p>
        </div>

        {isSubscriptionActive() && (
          <div className="asp-active-banner">
            <FaCheckCircle className="asp-active-banner-icon" />
            <span className="asp-active-banner-text">
              <strong>Active Plan: {currentSub.plan_name}</strong> — Expires on <strong>{formatDate(currentSub.expiry_date)}</strong>
            </span>
          </div>
        )}

        <Row className="g-4 justify-content-center">
          {plans
            .sort((a, b) => (PLAN_RANK[a.type] ?? 99) - (PLAN_RANK[b.type] ?? 99))
            .map(plan => {
            const pricing = plan.country_pricing;
            const isCurrent = currentSub?.plan_id === plan.id && isSubscriptionActive();
            const color = PLAN_COLORS[plan.type] || '#333';
            const gradient = PLAN_GRADIENTS[plan.type] || PLAN_GRADIENTS.free;
            const basePrice = pricing ? parseFloat(pricing.base_price) : 0;

            return (
              <Col key={plan.id} xs={12} md={6} lg={3}>
                <div className={`asp-plan-card ${isCurrent ? 'asp-current' : ''}`}>
                  {/* Gradient Header */}
                  <div className="asp-card-header" style={{ background: gradient }}>
                    {isCurrent && <span className="asp-active-tag">✓ Active</span>}
                    <div className="asp-card-icon">{PLAN_ICONS[plan.type]}</div>
                    <h4 className="asp-card-name">{plan.name}</h4>
                    <Badge className="asp-type-badge">{PLAN_LABELS[plan.type]}</Badge>
                    <div className="asp-validity">{plan.validity_days} days validity</div>
                  </div>

                  <div className="asp-card-body">
                    {/* Pricing */}
                    <div className="asp-pricing-section">
                      {plan.type === 'free' ? (
                        <div className="asp-price-free">FREE</div>
                      ) : (plan.type === 'gold' || plan.type === 'silver') ? (
                        <>
                          <div><span className="asp-price-amount">{basePrice > 0 ? basePrice.toLocaleString() : '0'}</span>
                            <span className="asp-price-currency">{pricing?.currency || 'USD'}</span></div>
                          <div className="asp-price-period">per {plan.validity_days} days</div>
                        </>
                      ) : plan.type === 'pay_per_click' ? (
                        <div className="asp-price-per-use">Pay per action from wallet</div>
                      ) : null}
                    </div>

                    {/* Features */}
                    <div className="asp-section-title">Features & Limits</div>
                    <div className="asp-features-list">
                      {/* Free Plan Static Features */}
                      {plan.type === 'free' && [
                        'Free Profile Creation',
                        'View Profiles',
                        'Send & Receive Messages (Free)',
                        'Search Matches',
                        'Edit Profile Anytime',
                        'Save / Shortlist Profiles',
                        'Match Alerts & Notifications',
                        'Send Alerts Without Contact Sharing',
                        'Upload Photos',
                      ].map((feat, idx) => (
                        <div key={`free-static-${idx}`} className="asp-feature-row">
                          <div className="asp-feature-left">
                            <FaCheckCircle className="asp-feature-icon" style={{ color: '#4caf50' }} />
                            <span className="asp-feature-label">{feat}</span>
                          </div>
                        </div>
                      ))}

                      {/* Gold Plan Static Features */}
                      {plan.type === 'gold' && [
                        'Unlock Contact Details (Unlimited)',
                        'Reveal WhatsApp Number (Unlimited)',
                        'Direct Call Access',
                        'Unlimited Direct Messaging',
                        'Send Alerts With Contact Details To Silver/Gold Candidates',
                        'Video Call Unlock',
                        'See Who Viewed Your Profile',
                        'Boost Profile (Weekly Free)',
                        'Top Listing Priority',
                        'Premium Gold Badge Display',
                        'Priority in Match Suggestions'
                      ].map((feat, idx) => (
                        <div key={`gold-static-${idx}`} className="asp-feature-row">
                          <div className="asp-feature-left">
                            <FaCheckCircle className="asp-feature-icon" style={{ color: '#f4a903' }} />
                            <span className="asp-feature-label">{feat}</span>
                          </div>
                        </div>
                      ))}

                      {/* Silver Plan Static Features */}
                      {plan.type === 'silver' && [
                        'Unlock Contact Details (Limited)',
                        'Reveal WhatsApp Number (Limited)',
                        'Direct Call Access',
                        'Direct Messaging (Limited)',
                        'Send Alerts With Contact Details To Silver/Gold Candidates',
                        'See Who Viewed Your Profile',
                        'Boost Profile (Monthly Free)',
                        'Top Listing (Limited Days)',
                        'Priority Match Suggestions',
                        'Verified Badge (Standard)'
                      ].map((feat, idx) => (
                        <div key={`silver-static-${idx}`} className="asp-feature-row">
                          <div className="asp-feature-left">
                            <FaCheckCircle className="asp-feature-icon" style={{ color: '#8E9AAF' }} />
                            <span className="asp-feature-label">{feat}</span>
                          </div>
                        </div>
                      ))}

                      {/* Pay Per Click Static Features */}
                      {plan.type === 'pay_per_click' && [
                        'Unlock Contact Details (Per Profile)',
                        'Reveal WhatsApp Number (Per Click)',
                        'Direct Call Access (Per Click)',
                        'Send Alerts Without Contact Sharing',
                        'Boost Profile (3/7 Days)',
                        'Top Listing (Per Day)',
                        'Video Call Unlock',
                        'Verified Badge (After KYC - Paid)',
                        'Pay only for what you use',
                        'Wallet never expires'
                      ].map((feat, idx) => (
                        <div key={`ppc-static-${idx}`} className="asp-feature-row">
                          <div className="asp-feature-left">
                            <FaCheckCircle className="asp-feature-icon" style={{ color: '#0d6efd' }} />
                            <span className="asp-feature-label">{feat}</span>
                          </div>
                        </div>
                      ))}

                      {plan.features?.map(f => {
                        const used = getUsage(f.feature_key);
                        return (
                          <div key={f.id} className="asp-feature-row">
                            <div className="asp-feature-left">
                              <FaCheckCircle className="asp-feature-icon" style={{ color }} />
                              <span className="asp-feature-label">{f.feature_label.replace(/\s*\((Employer|Candidate|Agency|Job\s*Seeker)\)/gi, '').replace('Job Poster Contact', 'Employer Contact')}</span>
                            </div>
                            <div className={`asp-feature-value d-flex align-items-end ${plan.type === 'pay_per_click' ? 'asp-feature-value-inline' : 'flex-column'}`}>
                              <>
                                {isCurrent && f.usage_limit > 0 && <span className={`asp-feature-usage ${plan.type === 'pay_per_click' ? '' : 'mb-1'}`}>{used}/{f.usage_limit}</span>}
                                {!isCurrent && <span className={`asp-feature-limit-badge ${plan.type === 'pay_per_click' ? '' : 'mb-1'}`}>{f.usage_limit === 0 ? 'Unlimited' : `${f.usage_limit} uses`}</span>}
                                {isCurrent && f.usage_limit === 0 && <span className={`asp-feature-limit-badge ${plan.type === 'pay_per_click' ? '' : 'mb-1'}`}>Unlimited</span>}
                              </>
                              {plan.type === 'pay_per_click' && (
                                <span style={{ fontSize: '0.8rem', color: '#666', whiteSpace: 'nowrap' }}>{pricing?.currency || ''} {Number(f.price_per_unit)}/use</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {(!plan.features || plan.features.length === 0) && (
                        <p style={{ color: '#999', textAlign: 'center', padding: '16px 0', fontSize: '0.85rem' }}>No features included</p>
                      )}
                    </div>

                    <div className="asp-card-footer">
                      {/* Wallet (PPC only) */}
                      {plan.type === 'pay_per_click' && (
                        <div className="asp-wallet-box">
                          <div className="asp-wallet-label">Wallet Balance</div>
                          <div>
                            <span className="asp-wallet-amount">{Number(walletBalance)}</span>
                            <span className="asp-wallet-currency">{pricing?.currency || 'AED'}</span>
                          </div>
                          <Button variant="link" className="asp-wallet-topup-btn p-0 text-decoration-none"
                            onClick={(e) => { e.stopPropagation(); setTopUpModal(true); }}>
                            + Add Money To Wallet
                          </Button>
                        </div>
                      )}

                      {/* Free Plan Important Note */}
                      {plan.type === 'free' && (
                        <div className="asp-free-note" style={{
                          background: '#fef9f0', border: '1px solid #f0dbb8', borderRadius: '8px',
                          padding: '14px 16px', textAlign: 'left'
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#7a5c2e' }}>
                            <FaLock style={{ fontSize: '0.85rem' }} /> <span>IMPORTANT NOTE</span>
                          </div>
                          <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6', color: '#7a5c2e', fontSize: '0.78rem' }}>
                            <li>Free users cannot see contact details</li>
                            <li>Only subscribed members can contact directly</li>
                          </ul>
                        </div>
                      )}

                      {/* Coupon Box (Gold/Silver) */}
                      {(plan.type === 'gold' || plan.type === 'silver') && !isCurrent && (
                        <div className="asp-wallet-box" style={{ background: '#fffcf5', border: '1px dashed #f4a903' }}>
                          <div className="asp-wallet-label mb-2" style={{ color: '#d48f00' }}>Have a Coupon?</div>
                          <div className="d-flex gx-2">
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Enter Code"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              style={{ borderRadius: '4px 0 0 4px', border: '1px solid #f4a903', boxShadow: 'none' }}
                              disabled={couponStatus?.applied}
                            />
                            <Button
                              variant="warning"
                              size="sm"
                              style={{ borderRadius: '0 4px 4px 0', background: '#f4a903', color: '#fff', border: 'none' }}
                              onClick={() => handleApplyCoupon(plan)}
                              disabled={applyingCoupon || couponStatus?.applied}
                            >
                              {applyingCoupon ? <Spinner size="sm" animation="border" /> : couponStatus?.applied ? 'Applied' : 'Apply'}
                            </Button>
                          </div>
                          {couponStatus && !couponStatus.applied && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.75rem' }}>{couponStatus.message}</div>
                          )}
                          {couponStatus && couponStatus.applied && (
                            <div className="text-success mt-1 fw-bold" style={{ fontSize: '0.75rem' }}>
                              {couponStatus.discount}% Discount Applied!
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Button */}
                      <button className={`asp-activate-btn ${isCurrent ? 'asp-activated' : ''} ${isDowngrade(plan) ? 'asp-downgrade-disabled' : ''}`}
                        style={{ background: isCurrent ? undefined : isDowngrade(plan) ? '#ccc' : gradient, cursor: isDowngrade(plan) ? 'not-allowed' : undefined }}
                        onClick={() => handleSubscribe(plan)}
                        disabled={(subscribing && selectedPlanId === plan.id) || isDowngrade(plan)}>
                        {subscribing && selectedPlanId === plan.id
                          ? <><Spinner size="sm" animation="border" /> Processing...</>
                          : isCurrent ? <><FaCheckCircle /> Plan Activated</>
                            : isDowngrade(plan) ? 'Activate This Plan'
                              : 'Activate This Plan'}
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
          {plans.length === 0 && <Col><p className="text-center text-muted py-5">No plans available at this time.</p></Col>}
        </Row>

        {/* Top-up Modal */}
        <Modal show={topUpModal} onHide={() => setTopUpModal(false)} centered className="asp-topup-modal">
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">💰 Top-up Wallet</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">Add funds to your wallet for Pay Per Use features.</p>

            <div className="asp-amount-grid mb-4">
              {[500, 1000, 1500, 2000].map(amount => (
                <div
                  key={amount}
                  className={`asp-amount-box ${topUpAmount === amount.toString() ? 'active' : ''}`}
                  onClick={() => setTopUpAmount(amount.toString())}
                >
                  <span className="asp-amount-value">{amount}</span>
                  <span className="asp-amount-currency">AED</span>
                </div>
              ))}
            </div>

            <Form.Group className="mb-4">
              {/* <Form.Label className="fw-bold small text-muted">Amount</Form.Label> */}
              <Form.Control type="number" placeholder="Enter amount" className="asp-topup-input"
                value={topUpAmount} onChange={(e) => setTopUpAmount(e.target.value)} />
            </Form.Group>
            <button className="asp-topup-confirm-btn" onClick={handleTopUp} disabled={processingTopUp}>
              {processingTopUp ? <><Spinner size="sm" animation="border" className="me-2" /> Processing…</> : 'Confirm Top-up'}
            </button>
          </Modal.Body>
        </Modal>

        <div className="text-center mt-5 mb-4">
          <Button variant="outline-secondary" className="asp-back-btn shadow-sm" onClick={() => navigate('/candidates')}>← Go Back</Button>
        </div>
      </Container>
    </>
  );
};

export default SubscriptionPlans;
